import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import auth from 'helpers/auth';
import _ from 'lodash';
import { Modal, ModalBody, ModalHeader, FormGroup, Button } from 'reactstrap';
import Datetime from 'react-datetime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faGavel, faUnlock, faRedo, faCalendarAlt, faQuestionCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import {
  API,
  BASE_URL,
  DEFAULT_PAGE_SIZE,
  SEARCH_DELAY_MS,
  NO_FILTERED_ID,
  ESTADOS_LINEAS,
  BACKEND_DF,
  FRONT_DF_1,
  ESTADOS_MOVIMIENTOS,
  RAMO_FECU_CREDITO_EXPORTACION_NEMOTECNICO
} from 'CONST';
import {
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
} from 'helpers/tables';
import { buildFilterParams, apelable } from 'helpers/helpers';
import store from 'redux-store/store';
import Icon from "../../../UI/Icon";
import isUserHasProfile from '../../../../helpers/profileValidator';
import { LINEA_CREDITO_WRITE } from '../../../../helpers/profilePermission';
import BaseTable from '../../../shared/BaseTable';
import FilterSelect from '../../../shared/FilterSelect';
import { formatRut } from '../../../../helpers/rutValidator';
import { put } from '../../../../helpers/requests';
import { getCountryFlag } from '../../../../helpers/countries'
import TableLink from '../../../shared/TableLink';
import SafeBtn from '../../../shared/SafeBtn';

const returnToUrl = '/asegurado/lineas-credito/';

const CreditPolicyDeudorLineasTable = ({
  client,
  vigencia,
  poliza,
  estadoLineaOptions,
  isPolizaVigente,
  estadoFilter,
  onEstadoFilterChange,
  loading,
  setLoading,
  hideLoadingText,
  lastEndosoCredito,
  isOnBorrador
}) => {
  const [lineas, setLineas] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterParams, setFilterParams] = useState('');
  const [vigenciaID, setVigenciaID] = useState(-1);
  const [isRetryEnabled, setIsRetryEnabled] = useState(true);
  // Modal states cambio fecha de vigencia
  const [modalStates, setModalStates] = useState({
    displayModalChangeDate: false,
    selectedMovimiento: -1,
    selectedBeginDate: null,
    originalSelectedBeginDate: null,
    selectedEndDate: null,
    originalSelectedEndDate: null,
    invalidDates: false,
    updateDateError: false,
    updateDateLoading: false,
    updateDateSuccessful: false,
  });

  const clienteRef = useRef(client && client.id ? client.id : null)

  const isCurrent = useRef(true);
  const tableRef = useRef();
  const filterParamsRef = useRef('');
  const vigenciaIDRef = useRef('');
  const lastEstadoFilter = useRef(estadoFilter); // Prevenir doble fetch

  const {user} = store.getState().login

  const isExportacion = poliza.ramo_fecu_nemotecnico === RAMO_FECU_CREDITO_EXPORTACION_NEMOTECNICO;
  const exportacionParam = isExportacion ? '&exportacion=true' : '';

  const fetchLineas = (state) => {
    if (vigencia.id) {
      setLoading(true);
      setVigenciaID(vigencia.id);
  
      // Se aplican los filtros.
      let filters = buildFilterParams(state, [], ['deudor_name', 'deudor_rut']);
      filters = `?poliza_id=${poliza.id}&${filters}`;
      filters = vigencia.id !== -1 ? `${filters}&vigencia_id=${vigencia.id}` : `${filters}`;
      setFilterParams(filters);
  
      // Se hace la request.
      const url = `${BASE_URL}${API.lineasCredito}/por-vigencia/${filters}`;
  
      auth.fetch(url).then((result) => {
        const sortedResults = result.results.sort((a, b) => b.id - a.id);

        // Se verifica que el cliente y la vigencia no hayan cambiado mientras se carga.
        // También se verifica que los filtros sean los últimos ingresados, y no los últimos en ser retornados.
        if (isCurrent.current && vigencia.id === vigenciaIDRef.current && filters === filterParamsRef.current) {
          setLineas({ ...result, results: sortedResults });
        }
      }).finally(() => {
        setIsFiltering(false);
        setLoading(false);
      });
    }
  };

  const checkConsecutiveErrors = async (lineas) => {
    if (!lineas || !lineas.results || lineas.results.length === 0) {
      return false;
    }
    
    const errorLines = lineas.results.filter(line => 
      line.estado_data && line.estado_data.id === 4
    );
    
    if (errorLines.length === 0) {
      return false;
    }
    
    const results = await Promise.all(errorLines.map(async (line) => {
      const lineaId = line.linea_credito;
      const lineasCreditoUrl = `${BASE_URL}${API.movimientosLineaCredito}/?linea_credito_id=${lineaId}&client_id=${line.linea_credito_data.cliente_id}`;
      
      try {
        const response = await auth.fetch(lineasCreditoUrl);
        const movements = Array.isArray(response) ? response.sort((a, b) => b.id - a.id) : [];        
        if (movements.length < 3) {
          return false;
        }
        
        // revisa los últimos 3 movimientos
        const lastThreeMovements = movements.slice(0, 3); 
        const allLastThreeAreErrors = lastThreeMovements.every(
          movement => movement.estado_data && movement.estado_data.id === 4
        );
        return allLastThreeAreErrors;
      } catch (error) {
        console.error(`Error fetching movements for credit line ${lineaId}:`, error);
        return false;
      }
    }));
    
    return results.some(result => result === true);
  };
  
  useEffect(() => {
    const checkRetry = async () => {
      if (lineas && lineas.results) {
        const canRetry = await checkConsecutiveErrors(lineas);
        setIsRetryEnabled(canRetry);
      }
    };
    
    checkRetry();
  }, [lineas]);

  const fetchDataWithDebounce = _.debounce(fetchLineas, SEARCH_DELAY_MS);
  const fetchStrategy = (tableState) => {
    if (isFiltering) {
      return fetchDataWithDebounce(tableState);
    }
    return fetchLineas(tableState);
  };

  const onFilteredChange = () => {
    setIsFiltering(true);
  };

  const updateModalState = (updates) => {
    setModalStates(prevState => ({ ...prevState, ...updates }));
  };

  const onChangeDateModalOpen = (id, beginDate, endDate) => {
    updateModalState({
      displayModalChangeDate: true,
      selectedMovimiento: id,
      selectedBeginDate: moment(beginDate, FRONT_DF_1),
      originalSelectedBeginDate: beginDate,
      selectedEndDate: moment(endDate, FRONT_DF_1),
      originalSelectedEndDate: endDate,
      updateDateLoading: false,
      updateDateError: false,
      updateDateSuccessful: false,
    })
  };

  const daysSince = (dateString) => {
    const [day, month, year] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    const result = (Date.now() - date.getTime()) / (24 * 60 * 60 * 1000);
    return result;
  };

  const onChangeDataModalClose = () => {
    if (!modalStates.updateDateLoading) updateModalState({ displayModalChangeDate: false });
  }

  const onChangeDateUpdate = () => {
    updateModalState({
      updateDateLoading: true,
      updateDateError: false,
    });

    const url = `${BASE_URL}${API.lineasCredito}/fecha-vigencia/?movimiento_id=${modalStates.selectedMovimiento}`;

    let data = {};

    if (modalStates.selectedBeginDate.format(FRONT_DF_1) !== modalStates.originalSelectedBeginDate) {
      data = { fecha_vigencia: modalStates.selectedBeginDate.format(BACKEND_DF) };
    }

    if (modalStates.selectedEndDate.format(FRONT_DF_1) !== modalStates.originalSelectedEndDate) {
      data = { ...data, fecha_fin_vigencia: modalStates.selectedEndDate.format(BACKEND_DF) };
    }

    put(url, data)
      .then(() => {
        fetchLineas(tableRef.current.state);
        updateModalState({ updateDateSuccessful: true });

        _.debounce(() => updateModalState({ displayModalChangeDate: false }), 2000)();
      })
      .catch(() => {
        updateModalState({ updateDateError: true });
      })
      .finally(() => {
        updateModalState({ updateDateLoading: false });
      });
  };

  const handleEstadoFilterChange = (value) => {
    lastEstadoFilter.current = value;

    if (onEstadoFilterChange) {
      onEstadoFilterChange(value);
    }
  }

  useEffect(() => {
    return () => {
      // Se llama cuando el componente está a punto de desmontarse.
      if(client && client.id) {
        isCurrent.current = client.id === clienteRef.current;
        clienteRef.current = client.id;
      }
    };
  }, [client]);

  useEffect(() => {
    // Se actualiza filterParamsRef y vigenciaIDRef.
    filterParamsRef.current = filterParams;
    vigenciaIDRef.current = vigenciaID;
  }, [filterParams, vigenciaID]);

  useEffect(() => {
    // Si se modifica la vigencia, se hace el fetch.
    if (vigenciaIDRef && vigencia && vigenciaIDRef.current !== vigencia.id) {
      fetchStrategy(tableRef.current.state);
    }
  }, [vigencia.id]);

  useEffect(() => {
    updateModalState({
      invalidDates: modalStates.selectedBeginDate >= modalStates.selectedEndDate,
    });
  }, [modalStates.selectedBeginDate, modalStates.selectedEndDate]);

  useEffect(() => {
    if (tableRef.current && estadoFilter !== lastEstadoFilter.current) {
      lastEstadoFilter.current = estadoFilter;

      const currentFiltered = tableRef.current.state.filtered || [];
      const otherFilters = currentFiltered.filter(f => f.id !== 'linea_credito__estado__id');
      
      const newFiltered = estadoFilter === NO_FILTERED_ID 
        ? otherFilters 
        : [...otherFilters, { id: 'linea_credito__estado__id', value: estadoFilter }];
      
      tableRef.current.setStateWithData({ filtered: newFiltered });
    }
  }, [estadoFilter]);

  const columns=[
    {
      Header: headerOperation('Razón Social'),
      id: 'deudor_name',
      accessor: 'linea_credito_data.deudor_name',
      width: 123,
      accessor: (i) => {
        return (
          <span title="ver">
            <Link
              className="d-block table-cell table-link text-left wrap"
              to={{
                pathname: `/asegurado/lineas-credito/detalle/${i.linea_credito_data.id}`,
                state: { returnToUrl },
              }}
            >
              { i.linea_credito_data.deudor_name }
            </Link>
          </span>
        );
      },
      sortable: false,
    },
    {
      Header: headerOperation(isExportacion ? 'N° Documento' : 'RUT'),
      id: 'deudor_rut',
      accessor: 'linea_credito_data.deudor_rut',
      width: isExportacion ? 150 : 123,
      Cell: (cell) => {
        const countryCode = cell.original.linea_credito_data.deudor_pais_code;

        if (!isExportacion || !countryCode || countryCode === 'CL') {
          return cellOperation(formatRut(cell.value));
        }

        return cellOperation(
          <>
            {cell.value}
            <Icon
              id={`pais-icon-${cell.original.id}`}
              placement="right"
              tooltipText={cell.original.linea_credito_data.deudor_pais_name}
            >
              {getCountryFlag(countryCode)}
            </Icon>
          </>
        );
      },
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation('Tipo', 'Deudor'),
      id: 'tipo',
      accessor: 'linea_credito_data.tipo_deudor',
      width: 123,
      Cell: (cell) => cellOperation(cell.value),
      style: {
        borderRight: '1px solid #CCCCCC'
      },
      getHeaderProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      getFilterProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      sortable: false,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Última', 'Solicitud'),
      id: 'tipo_solicitud',
      accessor: i => {
        if (i.tipo?.nemotecnico && ["deshabilitar", "reducir", "bloqueo"].includes(i.tipo.nemotecnico)) {
          return cellOperation("-", "center");
        }
        return cellOperation(
          i.is_migration ? (
            <div>
              {i.tipo.descripcion}
              <Icon
                id={`check-${i.id}`}
                placement="right"
                tooltipText="Este movimiento fue cargado en una migración histórica. Dirigirse al sitio de Credex para buscar el historial previo."
              >
                <FontAwesomeIcon style={{color: "var(--orsan-blue)"}} icon={faQuestionCircle} />
              </Icon>
            </div>
          ) : i.tipo.descripcion,
          "center"
        );
      },
      width: 123,
      Cell: (cell) => cellOperation(cell.value),
      sortable: false,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Fecha Último', 'Estado'),
      id: 'fecha_respuesta',
      accessor: 'fecha_respuesta',
      width: 123,
      Cell: (cell) => {
        if (cell.original && cell.original.estado_data &&
          cell.original.estado_data.nemotecnico === ESTADOS_LINEAS.pendiente) {
          return cellOperation('-');
        }
        return cellOperation(cell.value);
      },
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation("Es", "Temporal"),
      id: "es_linea_temporal",
      accessor: i => {
        //console.log("terminoVigenciaPoliza: ", terminoVigenciaPoliza);
        //console.log("i.fecha_fin_vigencia: ", i.fecha_fin_vigencia);
        if (!poliza.termino_vigencia || !i.fecha_fin_vigencia || i.monto_otorgado === 0) {
          return cellOperation("-", "center");
        }    
        const formatoISO = new Date(poliza.termino_vigencia);
        const [dia, mes, ano] = i.fecha_fin_vigencia.split("-");
        const formatoFechaFin = new Date(`${ano}-${mes}-${dia}`);
        
        return cellOperation(
          formatoFechaFin.getTime() !== formatoISO.getTime() ? "Si" : "No",
          "center"
        );
      },
      width: 123,
      sortable: false,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Monto', `Vigente (${poliza.moneda_asegurada})`),
      id: 'monto_vigente',
      accessor: 'monto_vigente',
      width: 123,
      Cell: (cell) => cellNumOperation(cell.value),
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Monto', `Solicitado (${poliza.moneda_asegurada})`),
      id: 'monto_solicitado',
      accessor: 'monto_solicitado',
      width: 123,
      Cell: (cell) => {
        // if (cell.original && cell.original.estado_data &&
        //   cell.original.estado_data.nemotecnico === ESTADOS_LINEAS.bloqueado) {
        //   return cellNumOperation(null);
        // }
        return cellNumOperation(cell.value);
      },
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Monto', `Otorgado (${poliza.moneda_asegurada})`),
      id: 'monto_otorgado',
      accessor: 'monto_otorgado',
      width: 123,
      Cell: (cell) => cellNumOperation(cell.value),
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation("Observaciones del", "Asegurado"),
      id: 'observacion',
      accessor: 'observacion',
      width: 123,
      Cell: (cell) => cellOperation(cell.value, 'left'),
      style: {
        borderRight: '1px solid #CCCCCC'
      },
      getHeaderProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      getFilterProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      sortable: false,
      filterable: false,
    },
    {
      Header: headerOperation("Estado"),
      id: "linea_credito__estado__id",
      accessor: (row) => {
        const estado = row.linea_credito_data?.estado_linea?.nombre;
        if (estado === "Limitada") return "Aprobada Parcial";
        if (estado === "Bloqueado") return "Bloqueada";
        return estado;
      },
      width: 123,
      Cell: (row) => cellOperation(row.value, "center"),
      Filter: ({ filter, onChange }) => (
        <FilterSelect
          filter={filter}
          defaultOption={{ label: "Todos", value: NO_FILTERED_ID }}
          onFilterChange={(value) => {
            onChange(value);
            handleEstadoFilterChange(value);
          }}
          options={estadoLineaOptions.map((elem) => ({
            label: elem.nombre === "Bloqueado" ? "Bloqueada" : 
                   elem.nombre === "Aprobado Parcial" ? "Aprobada Parcial" : elem.nombre,
            value: elem.id,
          }))}
        />
      ),
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation('Fecha Inicio', 'de Cobertura'),
      id: 'inicio_vigencia',
      accessor: (i) => {
        if (i.monto_vigente === 0) {
          return '-';
        }
        return i.fecha_vigencia;
      },
      width: 123,
      Cell: (cell) => cellOperation(cell.value),
      sortable: false,
      filterable: false,
    },
        {
      Header: headerDoubleRowOperation('Fecha Fin', 'de Cobertura'),
      id: 'fin_cobertura',
      accessor: (i) => {
        if (i.monto_vigente === 0) {
          return '-';
        }
        return i.fecha_fin_vigencia;
      },
      width: 123,
      Cell: (cell) => cellOperation(cell.value),
      sortable: false,
      filterable: false,
    },
    {
      Header: headerOperation("Resolución"),
      id: "resolucion",
      accessor: "estado_data",
      width: 136,
      Cell: (row) => {

        if (row.original.tipo?.nemotecnico && ["deshabilitar"].includes(row.original.tipo.nemotecnico)) {
          return cellOperation("-", "center");
        }
        
        return cellOperation(
          row.value ? (
            <div>
              {row.value.descripcion}
              &nbsp;
              {row.value.nemotecnico === ESTADOS_MOVIMIENTOS.error && (
                <>
                  <Icon
                    id={`obs-${row.original.id}`}
                    placement="right"
                    tooltipText={(
                      <div>
                        Ocurrió un error y no se pudo enviar su solicitud a analizar.
                      </div>
                    )}
                  >
                    <FontAwesomeIcon style={{ color: "var(--orsan-blue)" }} icon={faInfoCircle} />
                  </Icon>
                  <div>No se pudo procesar su solicitud. Por favor vuelva a intentarlo.</div>
                </>
              )}
            </div>
          ) : "Pendiente",
          "center"
        );
      },
      style: {
        borderRight: '1px solid #CCCCCC'
      },
      getHeaderProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      getFilterProps: () => ({ style: { borderRight: '1px solid #CCCCCC' } }),
      sortable: false,
      filterable: false,
      minWidth: 120,
      show: false,
    },
    {
      Header: headerOperation('Acciones'),
      id: 'acciones',
      accessor: (i) => (
        <div style={{ textAlign: 'center' }}>
          <TableLink
            title="ver"
            url={`/asegurado/lineas-credito/detalle/${i.linea_credito_data.id}`}
            icon={faEye}
            returnTo={returnToUrl}
          />
          {isUserHasProfile(LINEA_CREDITO_WRITE) &&   
            (isPolizaVigente() || 
            (poliza.borrador &&  moment(lastEndosoCredito.termino_vigencia).isAfter(moment()) &&
            lastEndosoCredito.estado === 'borrador')) &&
            i.linea_credito_data.estado_linea.nemotecnico !== ESTADOS_LINEAS.pendiente && (
            <>
              {([ESTADOS_LINEAS.aprobada, ESTADOS_LINEAS.limitada, ESTADOS_LINEAS.reducida]
                .includes(i.linea_credito_data.estado_linea.nemotecnico)) &&  (
                <>
                  &nbsp;&nbsp;
                  <TableLink
                    title="solicitar aumento"
                    url={`/asegurado/linea-credito/${i.linea_credito_data.poliza_id}/?linea_id=${i.linea_credito_data.id}${exportacionParam}`}
                    icon={faEdit}
                    returnTo={returnToUrl}
                  />
                </>
              )}
              {i.linea_credito_data.estado_linea.nemotecnico === ESTADOS_LINEAS.error && (
                <>
                  &nbsp;&nbsp;
                  <TableLink
                    title={!isRetryEnabled ? "reintentar solicitud" : "Demasiados intentos fallidos, contactar a servicios Ti"}
                    url={`/asegurado/linea-credito/${i.linea_credito_data.poliza_id}/?linea_id=${i.linea_credito_data.id}&retry${exportacionParam}`}
                    icon={faRedo}
                    // disabled={!user.is_orsan}
                    disabled={isRetryEnabled}
                    returnTo={returnToUrl}
                  />
                </>
              )}
              {i.linea_credito_data.estado_linea.nemotecnico === ESTADOS_LINEAS.bloqueado && (
                <>
                  &nbsp;&nbsp;
                  <TableLink
                    title="solicitar desbloqueo"
                    icon={i.fecha_solicitud && daysSince(i.fecha_solicitud) > 60 ? faEdit : faUnlock}
                    url={`/asegurado/linea-credito/${i.linea_credito_data.poliza_id}/?linea_id=${i.linea_credito_data.id}&unlock${exportacionParam}`}
                    returnTo={returnToUrl}
                  />
                </>
              )}
              {i.linea_credito_data.estado_linea.nemotecnico === ESTADOS_LINEAS.rechazada && (
                <>
                  &nbsp;&nbsp;
                  <TableLink
                    title={(apelable(i.fecha_respuesta) && i.is_migration == false) ? "apelar" : "solicitar reestudio"}
                    icon={(apelable(i.fecha_respuesta) && i.is_migration == false) ? faGavel : faEdit}
                    url={`/asegurado/linea-credito/${i.linea_credito_data.poliza_id}/?linea_id=${i.linea_credito_data.id}${exportacionParam}`}
                    returnTo={returnToUrl}
                  />
                </>
              )}
              {i.monto_vigente !== 0 && user.is_orsan && (
                <>
                  &nbsp;&nbsp;
                  <span title="Modificar fechas de vigencia">
                    <button
                      type="button"
                      className="table-link"
                      onClick={() => {onChangeDateModalOpen(i.id, i.fecha_vigencia, i.fecha_fin_vigencia)}}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </button>
                  </span>
                </>
							)}
            </>
          )}
        </div>
      ),
      width: 123,
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <>
      <BaseTable
        tableRef={tableRef}
        columns={columns}
        data={lineas.results}
        filterable
        loading={loading}
        onFetchData={fetchStrategy}
        onFilteredChange={onFilteredChange}
        pages={Math.ceil(lineas.count / DEFAULT_PAGE_SIZE)}
        resultCount={lineas.count}
        showPagination
        style={{ height: '63vh' }}
        hideLoadingText={hideLoadingText}
      />

      <Modal isOpen={modalStates.displayModalChangeDate} toggle={onChangeDataModalClose} size="lg" centered>
        <ModalHeader toggle={onChangeDataModalClose}>Modificar fechas de Cobertura</ModalHeader>
        <ModalBody>
          <FormGroup className="mb-5">
            <div className="row mb-2">
              <div className="col d-flex justify-content-center align-items-center">
                Inicio de Cobertura:
              </div>
              <div className="col">
                <Datetime
                  inputProps={{ disabled: modalStates.updateDateLoading || modalStates.updateDateSuccessful }}
                  onChange={(event) => {
                    updateModalState({ selectedBeginDate: event });
                  }}
                  value={modalStates.selectedBeginDate}
                  locale="es"
                  dateFormat={FRONT_DF_1}
                  timeFormat={false}
                  closeOnSelect
                />
              </div>
            </div>

            <div className="row">
              <div className="col d-flex justify-content-center align-items-center">
                Fin de Cobertura:
              </div>
              <div className="col">
                <Datetime
                  inputProps={{ disabled: modalStates.updateDateLoading || modalStates.updateDateSuccessful }}
                  onChange={(event) => {
                    updateModalState({ selectedEndDate: event });
                  }}
                  value={modalStates.selectedEndDate}
                  locale="es"
                  dateFormat={FRONT_DF_1}
                  timeFormat={false}
                  closeOnSelect
                />
              </div>
            </div>
          </FormGroup>

          {modalStates.updateDateError && (
            <p className="text-danger text-center">
              Se ha producido un error al intentar modificar la fecha. Por favor, vuelva a
              intentarlo más tarde.
            </p>
          )}

          {modalStates.invalidDates && (
            <p className="text-danger text-center">
              La fecha de inicio de vigencia no puede ser posterior a la fecha de fin.
            </p>
          )}

          {modalStates.updateDateSuccessful && (
            <p className="text-center">
              <strong>La modificación se realizó con éxito.</strong>
            </p>
          )}

          {!modalStates.updateDateSuccessful && (
            <div className="button-container">
              <div>
                <Button
                  className="warning-cancel"
                  onClick={() => updateModalState({ displayModalChangeDate: false })}
                  disabled={modalStates.updateDateLoading}
                >
                  Cancelar
                </Button>
              </div>
              <div>
                <SafeBtn
                  className="warning-accept"
                  onClick={() => onChangeDateUpdate()}
                  disabled={
                    ((modalStates.selectedBeginDate && modalStates.selectedBeginDate.format(FRONT_DF_1) === modalStates.originalSelectedBeginDate) &&
                    (modalStates.selectedEndDate && modalStates.selectedEndDate.format(FRONT_DF_1) === modalStates.originalSelectedEndDate)) ||
                    modalStates.invalidDates ||
                    modalStates.updateDateLoading
                  }
                >
                  {modalStates.updateDateLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Cargando</span>
                      </div>
                      &nbsp;&nbsp; Cargando
                    </>
                  ) : (
                    'Guardar'
                  )}
                </SafeBtn>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

CreditPolicyDeudorLineasTable.propTypes = {
  vigencia: PropTypes.shape({
    id: PropTypes.number,
    inicio_vigencia: PropTypes.string,
    termino_vigencia: PropTypes.string,
  }).isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
  poliza: PropTypes.shape({
    id: PropTypes.number,
    nemotecnico: PropTypes.string,
  }).isRequired,
  estadoLineaOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isPolizaVigente: PropTypes.func.isRequired,
  estadoFilter: PropTypes.number,
  onEstadoFilterChange: PropTypes.func,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  hideLoadingText: PropTypes.bool.isRequired,
  lastEndosoCredito: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOnBorrador: PropTypes.bool.isRequired
};

CreditPolicyDeudorLineasTable.defaultProps = {
  client: null,
  estadoFilter: NO_FILTERED_ID,
  onEstadoFilterChange: () => {},
};

export default CreditPolicyDeudorLineasTable;
